import { cn } from '@/lib/utils';
import { HealthcomDiscipline } from '@chiroup/core/types/HealthComLocation.type';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

type Props = {
  disciplines: HealthcomDiscipline[];
  onChange: (treatment: number) => void;
  value: number | null;
  providerOptions: any[];
};

const ScheduleTreatments: React.FC<Props> = ({ disciplines, onChange, value, providerOptions }) => {
  // Filter treatments by provider if needed
  const filteredDisciplines = disciplines
    ?.map(discipline => {
      const filteredTreatments = !providerOptions?.length
        ? discipline.treatments
        : discipline.treatments?.filter(t => {
            if (!t?.providers?.length) return true;

            return providerOptions?.some(p => t.providers?.includes(p.ID));
          }) || [];

      return {
        ...discipline,
        treatments: filteredTreatments,
      };
    })
    .filter(discipline => discipline.treatments.length > 0);

  return (
    <RadioGroup value={value} onChange={onChange}>
      <RadioGroup.Label>
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-900 whitespace-nowrap">Select a treatment</span>
          <div className="w-full border-t border-gray-300 grow ml-2" />
        </div>
      </RadioGroup.Label>

      <div className="mt-4 space-y-6">
        {filteredDisciplines.map(discipline => (
          <div key={discipline.ID} className="mb-6">
            {/* Discipline Header */}
            <div className="mb-3">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                {discipline.name}
              </h3>
              {discipline.description && (
                <p className="text-sm text-gray-500 mt-1">{discipline.description}</p>
              )}
            </div>

            {/* Treatments Grid */}
            <div className="grid grid-cols-1 gap-y-3 sm:gap-y-4 sm:grid-cols-2 sm:gap-x-4">
              {discipline.treatments.map(treatment => (
                <RadioGroup.Option
                  key={treatment.ID}
                  value={treatment.ID}
                  className={({ checked, active }) =>
                    cn(
                      checked ? 'border-transparent' : 'border-gray-300',
                      active ? 'border-primary ring-2 ring-primary' : '',
                      'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none'
                    )
                  }
                >
                  {({ active, checked }) => (
                    <>
                      <span className="flex flex-1">
                        <span className="flex flex-col">
                          <RadioGroup.Label
                            as="span"
                            className="block text-sm font-medium text-gray-900"
                          >
                            {treatment.name}{' '}
                            {treatment.onlineBookingDuration &&
                            typeof treatment.treatmentDuration === 'number'
                              ? `(${treatment.treatmentDuration} min)`
                              : ''}
                          </RadioGroup.Label>
                          <RadioGroup.Description
                            as="span"
                            className="mt-1 flex items-center text-sm text-gray-500"
                          >
                            {treatment.description}
                          </RadioGroup.Description>
                          {treatment.onlineBookingPrice && (
                            <RadioGroup.Description
                              as="span"
                              className="mt-3 text-sm font-medium text-gray-900"
                            >
                              Cash Price: {treatment.price}
                            </RadioGroup.Description>
                          )}
                        </span>
                      </span>
                      <CheckCircleIcon
                        className={cn(!checked ? 'invisible' : '', 'h-5 w-5 text-primary')}
                        aria-hidden="true"
                      />
                      <span
                        className={cn(
                          active ? 'border' : 'border-2',
                          checked ? 'border-primary' : 'border-transparent',
                          'pointer-events-none absolute -inset-px rounded-lg'
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </div>
        ))}
      </div>
    </RadioGroup>
  );
};

export default ScheduleTreatments;
